/**
 * AF-4082: funções puras da OBSERVAÇÃO EM LOTE e da tag "Interrupção notificada" do parecer PLD.
 *
 * A observação é o registro: não existe estado manual de notificação. A tag é DERIVADA
 * (item interrompido + observação preenchida). No momento do save, o snapshot grava
 * interruptionNotified/By/At por item para auditoria.
 *
 * Mantidas PURAS (sem React) para serem testadas isoladamente.
 */
import { normalizeRecommendation } from './statusLabels';

/** Campos de auditoria que o snapshot grava por documento interrompido com observação. */
export interface InterruptionFields {
    interruptionNotified?: boolean;
    interruptionNotifiedBy?: string;
    interruptionNotifiedAt?: string;
    observations?: string;
}

type ProviderState = { recommendation: string; interrupted?: boolean } | null;

/** Contrato mínimo que os helpers precisam ler de um documento do parecer. */
export interface InterruptibleDocument extends InterruptionFields {
    document: string;
    providers: {
        INOVAMIND: ProviderState;
        ESCAVADOR: ProviderState;
        QUOD: ProviderState;
    };
}

/** Mensagem pré-preenchida do modal (editável pelo analista). */
export const DEFAULT_INTERRUPTION_NOTE = 'Interrompido por problemas com levantamento de informações';

/** Mensagem é obrigatória: só vale com texto não-vazio após trim. */
export const isNoteValid = (text: string): boolean => text.trim().length > 0;

const providerStates = (doc: InterruptibleDocument): ProviderState[] => [
    doc.providers.INOVAMIND,
    doc.providers.ESCAVADOR,
    doc.providers.QUOD,
];

const hasProviderState = (doc: InterruptibleDocument, state: string): boolean =>
    providerStates(doc).some((p) => p != null && normalizeRecommendation(p.recommendation) === state);

/** Item interrompido: algum provider com flag `interrupted` ou estado INTERRUPTED (legado). */
export const isDocumentInterrupted = (doc: InterruptibleDocument): boolean =>
    providerStates(doc).some((p) => p != null && p.interrupted === true) ||
    hasProviderState(doc, 'INTERRUPTED');

export const hasObservation = (doc: InterruptionFields): boolean => (doc.observations ?? '').trim() !== '';

/** Tag derivada: interrompido E com observação (do lote ou digitada na linha). */
export const isInterruptionNotified = (doc: InterruptibleDocument): boolean =>
    isDocumentInterrupted(doc) && hasObservation(doc);

/** Acrescenta a mensagem em nova linha das observações, sem apagar nem duplicar. */
export const appendNoteToObservations = (observations: string | undefined, note: string): string => {
    const current = observations ?? '';
    if (current.trim() === '') return note;
    if (current.split('\n').some((line) => line.trim() === note)) return current;
    return `${current}\n${note}`;
};

/** Quem aplicou a observação e quando (auditoria da tag "Interrupção notificada"). */
export interface InterruptionAudit {
    user: string;
    nowIso: string;
}

/**
 * Carimba by/at quando o item é interrompido com observação; preserva o que já existe,
 * a menos que `overwrite` seja true (observação que passou de vazia para preenchida).
 */
export const stampInterruptionAudit = <T extends InterruptibleDocument>(
    doc: T,
    audit: InterruptionAudit,
    overwrite = false
): T => {
    if (!isInterruptionNotified(doc)) return doc;
    return {
        ...doc,
        interruptionNotifiedBy: overwrite ? audit.user : doc.interruptionNotifiedBy || audit.user,
        interruptionNotifiedAt: overwrite ? audit.nowIso : doc.interruptionNotifiedAt || audit.nowIso,
    };
};

/** Observação em lote: acrescenta a mensagem nos itens selecionados, de qualquer status. */
export const applyBatchObservation = <T extends InterruptibleDocument>(
    documents: T[],
    selected: Set<string>,
    note: string,
    audit?: InterruptionAudit
): T[] =>
    documents.map((doc) => {
        if (!selected.has(doc.document)) return doc;
        const updated = { ...doc, observations: appendNoteToObservations(doc.observations, note.trim()) };
        return audit ? stampInterruptionAudit(updated, audit, !hasObservation(doc)) : updated;
    });

/** Edição da observação de uma linha: carimba (sobrescrevendo) só quando passa de vazia para preenchida. */
export const applyObservationEdit = <T extends InterruptibleDocument>(
    doc: T,
    text: string,
    audit: InterruptionAudit
): T => {
    const updated = { ...doc, observations: text };
    if (hasObservation(doc) || !hasObservation(updated)) return updated;
    return stampInterruptionAudit(updated, audit, true);
};

/** Itens interrompidos sem observação: bloqueiam o salvamento do parecer. */
export const findInterruptedWithoutObservation = (documents: InterruptibleDocument[]): InterruptibleDocument[] =>
    documents.filter((doc) => isDocumentInterrupted(doc) && !hasObservation(doc));

const hasErrorProvider = (doc: InterruptibleDocument): boolean =>
    providerStates(doc).some((p) => p?.recommendation === 'ERROR');

/**
 * Documentos que ainda bloqueiam o save por erro: algum provider em ERROR e sem a
 * condição "interrompido com observação". Itens que não são interrompidos seguem bloqueando.
 */
export const countBlockingErrors = (documents: InterruptibleDocument[]): InterruptibleDocument[] =>
    documents.filter((doc) => !isInterruptionNotified(doc) && hasErrorProvider(doc));

/**
 * Snapshot de auditoria do save: itens interrompidos com observação recebem
 * interruptionNotified/By/At (by/at originais são preservados na reabertura);
 * os demais levam interruptionNotified: false e sem by/at. Não remove nenhum item.
 */
export const buildSnapshotDocuments = <T extends InterruptibleDocument>(
    documents: T[],
    user: string,
    nowIso: string
): T[] =>
    documents.map((doc) => {
        if (!isInterruptionNotified(doc)) {
            return {
                ...doc,
                interruptionNotified: false,
                interruptionNotifiedBy: undefined,
                interruptionNotifiedAt: undefined,
            };
        }
        return {
            ...doc,
            interruptionNotified: true,
            interruptionNotifiedBy: doc.interruptionNotifiedBy || user,
            interruptionNotifiedAt: doc.interruptionNotifiedAt || nowIso,
        };
    });

/** Agregado de auditoria do payload: itens com interrupção notificada e suas observações. */
export const buildInterruptionAggregate = (
    documents: InterruptibleDocument[]
): { interruptionNotifiedDocuments: { document: string; observation: string }[]; interruptionNotifiedCount: number } => {
    const interruptionNotifiedDocuments = documents.filter(isInterruptionNotified).map((doc) => ({
        document: doc.document,
        observation: doc.observations ?? '',
    }));

    return { interruptionNotifiedDocuments, interruptionNotifiedCount: interruptionNotifiedDocuments.length };
};
